// The links between notes in the project folders, worked out on the board from the runner's index, the way
// Obsidian works them out: [[a note]] by its name anywhere in the folders (the nearest first when two share a
// name), [[folder/a note]] by its path, ![[a picture.png]] by the file's whole name, and [words](a/path.md) from
// the note's own folder first. Pure, so the browser and the tests share it.

import type { FileEntry, FilesRoot, NoteLink } from "./files";
import { readProperties } from "./filenote";

/** A path's parts, whichever separator the computer uses. */
export const partsOf = (p: string): string[] => p.split(/[\\/]+/).filter(Boolean);

/** A file's name, its last part. */
export const nameOf = (p: string): string => {
  const parts = partsOf(p);
  return parts[parts.length - 1] ?? p;
};

const NOTE_EXT = /\.(md|markdown)$/i;
export const isNote = (p: string): boolean => NOTE_EXT.test(p);

/** A note's name as shown: without .md. Other files keep their extension. */
export const titleOf = (p: string): string => nameOf(p).replace(NOTE_EXT, "");

/** The folder a path is in. */
export function dirOf(p: string): string {
  const i = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
  return i > 0 ? p.slice(0, i) : p;
}

/** Where a path is: its project folder and its place inside it, with / between its parts; null when it is in none. */
export function placeIn(p: string, roots: FilesRoot[]): { root: FilesRoot; rel: string } | null {
  const norm = (x: string) => x.replace(/\\/g, "/").replace(/\/+$/, "");
  const n = norm(p);
  let best: { root: FilesRoot; rel: string } | null = null;
  for (const root of roots) {
    const r = norm(root.path);
    if (n === r) return { root, rel: "" };
    if (n.toLowerCase().startsWith(r.toLowerCase() + "/") && (!best || r.length > norm(best.root.path).length)) best = { root, rel: n.slice(r.length + 1) };
  }
  return best;
}

/** A link's target split into its file and the heading or block after #. */
export function splitTarget(t: string): { file: string; anchor: string | null } {
  const i = t.indexOf("#");
  if (i < 0) return { file: t.trim(), anchor: null };
  return { file: t.slice(0, i).trim(), anchor: t.slice(i + 1).trim() || null };
}

/** What the board knows to resolve links: each file by its name and by its path inside its folder, lowercased. */
export interface LinkTable {
  roots: FilesRoot[];
  /** by name: a note without .md, any file with its whole name */
  byName: Map<string, string[]>;
  /** by its path inside its folder, a note with and without .md */
  byRel: Map<string, string[]>;
  /** each file's place, by its full path */
  rel: Map<string, { root: FilesRoot; rel: string }>;
}

export function linkTable(files: Iterable<FileEntry>, roots: FilesRoot[]): LinkTable {
  const byName = new Map<string, string[]>();
  const byRel = new Map<string, string[]>();
  const rel = new Map<string, { root: FilesRoot; rel: string }>();
  const add = (m: Map<string, string[]>, k: string, p: string) => {
    const list = m.get(k);
    if (list) list.push(p);
    else m.set(k, [p]);
  };
  for (const f of files) {
    if (f.d) continue;
    const at = placeIn(f.p, roots);
    if (!at) continue;
    rel.set(f.p, at);
    const name = nameOf(f.p).toLowerCase();
    add(byName, name, f.p);
    const r = at.rel.toLowerCase();
    add(byRel, r, f.p);
    if (isNote(f.p)) {
      add(byName, name.replace(NOTE_EXT, ""), f.p);
      add(byRel, r.replace(NOTE_EXT, ""), f.p);
      // Obsidian's aliases: other names a note answers to, from its frontmatter
      if (f.n?.fm && /^alias(es)?\s*:/im.test(f.n.fm)) {
        const v = readProperties(f.n.fm)?.find((x) => /^alias(es)?$/i.test(x.key))?.value;
        for (const a of Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : []) if (a !== null && String(a).trim()) add(byName, String(a).trim().toLowerCase(), f.p);
      }
    }
  }
  return { roots, byName, byRel, rel };
}

/** How near two files are: the folders they share from their project folder down, more being nearer. */
function nearness(a: string, b: string, t: LinkTable): number {
  const pa = t.rel.get(a);
  const pb = t.rel.get(b);
  if (!pa || !pb) return -1;
  if (pa.root.path !== pb.root.path) return -1;
  const da = partsOf(pa.rel).slice(0, -1);
  const db = partsOf(pb.rel).slice(0, -1);
  let n = 0;
  while (n < da.length && n < db.length && da[n].toLowerCase() === db[n].toLowerCase()) n++;
  // the same folder is nearest of all
  return n + (da.length === n && db.length === n ? 1000 : 0);
}

/** The nearest of several files to the note linking to them: then the one fewest folders down, then by name. */
function nearest(list: string[], from: string, t: LinkTable): string {
  const depth = (p: string) => partsOf(t.rel.get(p)?.rel ?? p).length;
  return [...list].sort((a, b) => nearness(b, from, t) - nearness(a, from, t) || depth(a) - depth(b) || a.localeCompare(b))[0];
}

/** Resolve a path written relative to a folder, ../ and ./ included; null when it climbs out of its project folder. */
function joinRel(dir: string, rel: string): string | null {
  const out = partsOf(dir);
  for (const part of partsOf(rel)) {
    if (part === ".") continue;
    if (part === "..") {
      if (!out.length) return null;
      out.pop();
    } else out.push(part);
  }
  return out.join("/");
}

/**
 * The file a link points to, from the note it is in: the full path, or null when no file in the project folders
 * is the one it names (a link that goes nowhere). The anchor is the heading or block after #.
 */
export function resolveLink(link: Pick<NoteLink, "t" | "md">, from: string, t: LinkTable): { path: string | null; anchor: string | null } {
  const { file, anchor } = splitTarget(link.t);
  // [[#Heading]]: a place in the same note
  if (!file) return { path: from, anchor };
  const at = t.rel.get(from);
  const pick = (list: string[] | undefined) => (list && list.length ? nearest(list, from, t) : null);
  const want = file.replace(/\\/g, "/").toLowerCase();
  // a Markdown link, or one with a ./ or ../, from the note's own folder first
  if (at && (link.md || /^\.{1,2}\//.test(want))) {
    const rel = joinRel(partsOf(at.rel).slice(0, -1).join("/").toLowerCase(), want);
    if (rel !== null) {
      const hit = (t.byRel.get(rel) ?? t.byRel.get(rel.replace(NOTE_EXT, "")) ?? []).filter((p) => t.rel.get(p)?.root.path === at.root.path);
      if (hit.length) return { path: hit[0], anchor };
    }
  }
  if (want.includes("/")) {
    // a path inside a project folder, from its top: in the note's own project folder first
    const clean = want.replace(/^\/+/, "");
    const hit = pick(t.byRel.get(clean) ?? t.byRel.get(clean.replace(NOTE_EXT, "")));
    if (hit) return { path: hit, anchor };
    // as Obsidian does, a path that ends a file's path will do: "Board/Spec" finds "Projects/Board/Spec.md"
    const tail = [...t.byRel.keys()].filter((k) => k.endsWith("/" + clean) || k.endsWith("/" + clean.replace(NOTE_EXT, "")));
    const hits = tail.flatMap((k) => t.byRel.get(k) ?? []);
    if (hits.length) return { path: nearest([...new Set(hits)], from, t), anchor };
    return { path: null, anchor };
  }
  return { path: pick(t.byName.get(want)), anchor };
}

/** A link from one note to another, as the links pane shows it: the note it is in, its line and the words around it. */
export interface Backlink {
  from: string;
  line: number;
  text: string;
  embed: boolean;
}

/** What links to what, across the folders: every note's backlinks, and the links that go nowhere, by the note they are in. */
export function linkGraph(files: Iterable<FileEntry>, t: LinkTable): { back: Map<string, Backlink[]>; out: Map<string, (string | null)[]> } {
  const back = new Map<string, Backlink[]>();
  const out = new Map<string, (string | null)[]>();
  for (const f of files) {
    if (!f.n) continue;
    const targets: (string | null)[] = [];
    for (const l of f.n.links) {
      const { path } = resolveLink(l, f.p, t);
      targets.push(path);
      if (!path || path === f.p) continue;
      const list = back.get(path);
      const b = { from: f.p, line: l.l, text: l.x, embed: !!l.e };
      if (list) list.push(b);
      else back.set(path, [b]);
    }
    out.set(f.p, targets);
  }
  return { back, out };
}
